import { formatDigestDelta, formatDigestRate, formatSuiteFlows, type DigestFacts, type DigestKind } from '@chatsdet/types';

export type OverviewTone = 'success' | 'destructive' | 'muted';

export interface HeadlinePanelData {
  title: string;
  /** The big number, without its unit ("159", "85", "—"). */
  value: string;
  /** Rendered after the number in the accent colour ("%"); null when there is none. */
  unit: string | null;
  /** "was 154"; null when the previous period was not recorded. */
  was: string | null;
  delta: { text: string; tone: OverviewTone } | null;
  bar: {
    /** Colour of the first segment. The second segment (the gain) is always success-coloured. */
    base: 'neutral' | 'primary';
    /** Widths as percentages of the bar; empty when there is nothing to draw. */
    basePct: number;
    gainPct: number;
    /** Last period's position on the bar; null when unknown. */
    tickPct: number | null;
    leftLabel: string;
    rightLabel: string | null;
    ariaLabel: string;
  };
}

export interface SinceBoxData {
  key: string;
  label: string;
  value: string;
  sub: string | null;
  subTone: OverviewTone;
}

export interface DigestOverview {
  /** "today" / "this week". */
  current: string;
  /** "since yesterday" / "since last week". */
  since: string;
  /** "yesterday" / "last week". */
  previousWord: string;
  /** False when the digest carries no product-map size at all (an old row with no areas): the panels would only show zeros. */
  hasCapabilities: boolean;
  capabilities: HeadlinePanelData;
  coverage: HeadlinePanelData;
  boxes: SinceBoxData[];
}

const round1 = (value: number) => Math.round(value * 10) / 10;

const rateText = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1));

function toneOf(delta: number): OverviewTone {
  return delta > 0 ? 'success' : delta < 0 ? 'destructive' : 'muted';
}

function countDelta(delta: number, since: string): { text: string; tone: OverviewTone } {
  if (delta === 0) return { text: `No change ${since}`, tone: 'muted' };
  return { text: `${delta > 0 ? '+' : '−'}${Math.abs(delta)} ${since}`, tone: toneOf(delta) };
}

/** Everything the digest page's headline panels and "Since" boxes show, derived from the facts and the digest's kind. */
export function buildDigestOverview(facts: DigestFacts, kind: DigestKind): DigestOverview {
  const weekly = kind === 'weekly';
  const current = weekly ? 'this week' : 'today';
  const previousWord = weekly ? 'last week' : 'yesterday';
  const since = `since ${previousWord}`;
  const { total, mapped, previous, added, updated } = facts.capabilities;

  // Capabilities panel: the bar is the size of the map now, split into what was
  // already there and what is new. Removed capabilities show only in the delta.
  const existing = previous ? Math.min(previous.total, total) : total;
  const created = total - existing;
  const capabilities: HeadlinePanelData = {
    title: 'Capabilities',
    value: String(total),
    unit: null,
    was: previous ? `was ${previous.total}` : null,
    delta: previous ? countDelta(total - previous.total, since) : null,
    bar: {
      base: 'neutral',
      basePct: total > 0 ? (existing / total) * 100 : 0,
      gainPct: total > 0 ? (created / total) * 100 : 0,
      tickPct: null,
      leftLabel: previous ? `${existing} existing` : `${total} in total`,
      rightLabel: previous && created > 0 ? `+${created} new` : null,
      ariaLabel: previous
        ? `${total} capabilities: ${existing} existing, ${created} new ${since}`
        : `${total} capabilities`,
    },
  };

  // Coverage panel: percentage mapped now, with the gain since the last period
  // in green and a tick where the last period stood. A drop leaves the bar at
  // today's value with the tick showing where it was.
  const pct = total > 0 ? round1((mapped / total) * 100) : null;
  const prevPct = previous && previous.total > 0 ? round1((previous.mapped / previous.total) * 100) : null;
  const pctDelta = pct !== null && prevPct !== null ? round1(pct - prevPct) : null;
  const gain = pctDelta !== null && pctDelta > 0 ? pctDelta : 0;
  const deltaText = pctDelta === null ? null : formatDigestDelta(pctDelta);
  const coverage: HeadlinePanelData = {
    title: 'Coverage',
    value: pct === null ? '—' : rateText(pct),
    unit: pct === null ? null : '%',
    was: prevPct === null ? null : `was ${formatDigestRate(prevPct)}`,
    delta:
      pctDelta === null || deltaText === null
        ? null
        : pctDelta === 0
          ? { text: `No change ${since}`, tone: 'muted' }
          : { text: `${deltaText} ${since}`, tone: toneOf(pctDelta) },
    bar: {
      base: 'primary',
      basePct: pct === null ? 0 : pct - gain,
      gainPct: gain,
      tickPct: prevPct,
      leftLabel: total > 0 ? `${mapped} of ${total} covered` : 'No capabilities yet',
      rightLabel: prevPct === null ? null : `was ${formatDigestRate(prevPct)}`,
      ariaLabel:
        pct === null
          ? 'Coverage: no capabilities yet'
          : `Coverage ${formatDigestRate(pct)}: ${mapped} of ${total} capabilities covered${
              prevPct === null ? '' : `, was ${formatDigestRate(prevPct)} ${previousWord}`
            }`,
    },
  };

  const boxes: SinceBoxData[] = [];
  const box = (key: string, label: string, value: string, sub: string | null, subTone: OverviewTone = 'muted') =>
    boxes.push({ key, label, value, sub, subTone });

  if (added !== null) box('added', 'New capabilities', `+${added}`, since);
  if (updated !== null) box('updated', 'Updated capabilities', String(updated), `edited ${since}`);
  box('promoted', 'New Workflows', String(facts.flows.promoted), 'promoted to approved');
  if (pct !== null) {
    box(
      'coverage',
      'Coverage',
      prevPct === null ? formatDigestRate(pct) : `${formatDigestRate(prevPct)} → ${formatDigestRate(pct)}`,
      deltaText === null || pctDelta === null ? null : pctDelta === 0 ? 'No change' : deltaText,
      pctDelta === null ? 'muted' : toneOf(pctDelta)
    );
  }
  box('issues', 'Issues filed', String(facts.issues.filed), `${facts.issues.open} open`);

  const { completed, flowsTotal, flowsPassed } = facts.suites;
  box(
    'suites',
    'Suite runs',
    flowsTotal > 0 ? formatDigestRate((flowsPassed / flowsTotal) * 100) : '—',
    completed > 0 ? `${completed} ${completed === 1 ? 'run' : 'runs'} · ${formatSuiteFlows(facts.suites)}` : 'No suite runs'
  );

  return { current, since, previousWord, hasCapabilities: total > 0 || previous !== null, capabilities, coverage, boxes };
}
